import { useEffect, useRef, useState, DragEvent, ChangeEvent } from "react";
import useTranslator from "../../../../hooks/useTranslator";

const DOCUMENT_PLACEHOLDER_URL =
  "https://sdk.onfido.com/assets/icons/identity-icon.svg";

interface FileUploadScreenProps {
  title: string;
  subtitle: string;
  onFileSelect: (file: File | Blob) => Promise<void>;
  acceptLabel?: string;
}

const FileUploadScreen = ({
  title,
  subtitle,
  onFileSelect,
  acceptLabel,
}: FileUploadScreenProps) => {
  const { _t } = useTranslator();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(pendingFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [pendingFile]);

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleTriggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    clearFileInput();
  };

  const hasDraggedFiles = (event: DragEvent) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const handleDragEnter = (event: DragEvent) => {
    if (!hasDraggedFiles(event) || isSubmitting) return;

    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingFile(true);
  };

  const handleDragOver = (event: DragEvent) => {
    if (!hasDraggedFiles(event) || isSubmitting) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (event: DragEvent) => {
    if (dragDepthRef.current === 0) return;

    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingFile(false);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDraggingFile(false);

    if (isSubmitting) return;

    const file = event.dataTransfer.files?.[0];
    if (file) setPendingFile(file);
  };

  const handleUploadConfirm = async () => {
    if (!pendingFile || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onFileSelect(pendingFile);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`custom-kyc-upload-screen ${
        isDraggingFile ? "is-dragging-file" : ""
      }`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDraggingFile && (
        <div className="custom-kyc-upload-drop-overlay" aria-hidden="true">
          <div className="custom-kyc-upload-drop-message">
            {_t("Drop the file here")}
          </div>
        </div>
      )}

      {pendingFile ? (
        <div className="custom-kyc-upload-review">
          <h3>{_t("Review your image")}</h3>
          <div className="custom-kyc-upload-preview-frame">
            <img src={previewUrl} alt={_t("Document preview")} />
          </div>
        </div>
      ) : (
        <div className="custom-kyc-upload-intro">
          <div className="custom-kyc-upload-hero">
            <img src={DOCUMENT_PLACEHOLDER_URL} alt="" />
          </div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
      )}

      <div className="custom-kyc-upload-action-panel">
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptLabel || ".jpg,.jpeg,.png"}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {pendingFile ? (
          <>
            <button
              type="button"
              className="custom-kyc-upload-secondary-btn"
              disabled={isSubmitting}
              onClick={() => {
                setPendingFile(null);
                clearFileInput();
              }}
            >
              {_t("Redo")}
            </button>
            <button
              type="button"
              className="custom-kyc-upload-primary-btn"
              disabled={isSubmitting}
              onClick={handleUploadConfirm}
            >
              {isSubmitting ? _t("Uploading") : _t("Upload")}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="custom-kyc-upload-primary-btn"
            onClick={handleTriggerFileInput}
          >
            {_t("Upload photo")}
          </button>
        )}
      </div>
    </div>
  );
};

export default FileUploadScreen;
